/*
 * Zahlenleiste ("Social Proof") unter dem Hero der Startseite.
 *
 * HIER die Zahlen eintragen — sonst nirgends. Die Leiste auf der Startseite
 * liest ausschliesslich diese Datei.
 *
 * Regeln, damit nie etwas Erfundenes live geht:
 *   - Eine Kachel mit leerem `value` wird uebersprungen.
 *   - Bleiben weniger als zwei gefuellte Kacheln uebrig, faellt die ganze
 *     Leiste weg. Die Seite bleibt gueltig, es entsteht keine Luecke.
 *   - `value` ist reiner Text und wird genau so angezeigt, wie er hier steht.
 *     Also "27,000+" schreiben, nicht 27000 — es wird nichts umformatiert.
 *
 * Jede Zahl muss belegbar sein (DB-Query, Stripe, GA4). `source` ist nur fuer
 * uns intern und erscheint nirgends auf der Seite: dort gehoert hin, WOHER die
 * Zahl kommt und WANN sie gezogen wurde, damit sie beim naechsten Mal
 * reproduzierbar ist.
 */

/*
 * Erlaubte Symbole. Nur diese Namen funktionieren — ein Tippfehler laesst die
 * Kachel einfach ohne Symbol laufen, er bricht nichts.
 */
export type LandingSocialProofIcon =
  | 'visitors'
  | 'campaigns'
  | 'calendar'
  | 'globe'
  | 'chart'
  | 'shield'
  | 'target'
  | 'speed';

export interface LandingSocialProofStat {
  /** Angezeigte Zahl, z. B. "1.2B+", "190", "4.8/5". Leer = Kachel aus. */
  value: string;
  /** Kurze Beschriftung darunter, z. B. "Visitors delivered". */
  label: string;
  /** Symbol ueber der Zahl. Weglassen zeigt die Kachel ohne Symbol. */
  icon?: LandingSocialProofIcon;
  /** Optionale Praezisierung in kleiner Schrift, z. B. "since 2021". */
  note?: string;
  /** Nur intern: Herkunft + Datum der Zahl. Wird nicht angezeigt. */
  source?: string;
}

/** Ueberschrift ueber der Leiste. Leer lassen blendet die Zeile aus. */
export const LANDING_SOCIAL_PROOF_INTRO = 'Delivery to date';

export const LANDING_SOCIAL_PROOF_STATS: LandingSocialProofStat[] = [
  {
    value: '100M+',
    label: 'Visitors delivered',
    icon: 'visitors',
    note: '',
    source:
      'Prod-DB 2026-08-11, zwei unabhaengige Zaehler: '
      + 'SELECT SUM(delivered_visitors_total) FROM projects = 111.594.124; '
      + 'SELECT SUM(successful_hits) FROM project_stats = 104.877.820. '
      + 'Beide stimmen auf ~6 % ueberein, deshalb bewusst auf 100M+ abgerundet.',
  },
  {
    value: '5,100+',
    label: 'Campaigns launched',
    icon: 'campaigns',
    note: '',
    source:
      'Prod-DB 2026-08-11: SELECT COUNT(*) FROM projects '
      + 'WHERE delivered_visitors_total > 0 = 5.182 (nur Projekte mit echter '
      + 'Auslieferung; COUNT(*) gesamt waere 5.395).',
  },
  {
    value: '2019',
    label: 'Delivering since',
    icon: 'calendar',
    note: '',
    /*
     * Von Martin korrigiert (2026-08-11): Auslieferung laeuft seit 2019.
     * Die Datenbank kennt nur den aeltesten dort noch vorhandenen Account
     * (MIN(created_at) FROM users = 2020-02-10) und ist damit kein Beleg fuer
     * den Start, sondern nur fuer das Alter der aktuellen Nutzertabelle.
     */
    source: 'Angabe Martin 2026-08-11. DB-Untergrenze: aeltester User 2020-02-10.',
  },
  {
    /*
     * Noch offen: die DB-Abfrage ueber visitor_deliveries laeuft in den
     * statement_timeout, und "belieferte Laender" ist ohnehin die falsche
     * Zahl — werblich zaehlt, was in der Kampagnenanlage auswaehlbar ist.
     * Bitte dort abzaehlen und hier eintragen, dann erscheint die vierte
     * Kachel von selbst.
     */
    value: '',
    label: 'Countries targetable',
    icon: 'globe',
    note: '',
    source: 'Quelle: Laender-Auswahl in der Kampagnenanlage — Stand: ',
  },
];

/**
 * Nur die gefuellten Kacheln, und nur wenn mindestens zwei uebrig bleiben.
 * Eine einzelne Zahl wirkt duenner als gar keine.
 */
export const getVisibleSocialProofStats = (
  stats: LandingSocialProofStat[] = LANDING_SOCIAL_PROOF_STATS,
): LandingSocialProofStat[] => {
  const filled = stats.filter(
    (stat) => stat.value.trim().length > 0 && stat.label.trim().length > 0,
  );
  return filled.length >= 2 ? filled : [];
};
